import React, { useMemo, useCallback } from "react";
import NextLink from "next/link";
import { DynamicReportType } from "graphql/types";
import { BreakoutCellData, useBreakout } from "../BreakoutProvider";
import { useHighlight } from "../HighlightProvider";
import { getLinkForAccrualRevenueTotal } from "../util";
import InteractiveCell from "./InteractiveCell";
import { useDelta } from "../Filters/DeltaProvider";
import { FeatureFlag, isPosthogFeatureFlagEnabled } from "lib/analytics/featureFlags";
import Analytics from "lib/analytics/analytics";
import { constructHref } from "@puzzle/utils";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { reportError } from "lib/errors/errors";

export const BreakoutCell = (
  props: BreakoutCellData & {
    value: string;
    variant?: "footer";
  }
) => {
  const { categoriesByPermaKey, stickyOptions, row, rowsById, column, value, variant } = props;
  const isDiff = column.id.startsWith("Change");
  const node = row?.original;
  const { deltaOptions } = useDelta();
  const { setHighlightState, optionsForCell } = useHighlight();
  const { view, activeTab, filter } = stickyOptions;
  const { reportType, breakoutData, setBreakoutData, renderBreakout, link } = useBreakout({
    categoriesByPermaKey,
    stickyOptions,
    row,
    column,
    rowsById,
  });
  const { company, companies } = useActiveCompany();
  const companyId = company?.id ?? undefined;
  const timePeriod = useMemo(() => {
    return node?.balanceByColumn.find((p) => p.columnKey === column.id);
  }, [column.id, node?.balanceByColumn]);
  const hasTimePeriod = Boolean(
    timePeriod?.dateRange.fromInclusive && timePeriod?.dateRange.toInclusive
  );
  const hasChildren = Boolean(row?.canExpand && variant !== "footer");
  const hasBreakout = Boolean(!hasChildren && timePeriod?.pathToken && hasTimePeriod);
  const isAccrualRevenueSource = node?.lineType === "accrual_revenue_source";
  const options = useMemo(() => {
    if (!node) {
      return;
    }
    return optionsForCell(node, column);
  }, [column, node, optionsForCell]);
  const active = options?.active || (row === breakoutData?.row && column === breakoutData?.column);
  const handleHoverStart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement> | React.FocusEvent<HTMLButtonElement>) => {
      const ruleIndex = options?.metadata?.ruleIndex;
      if (typeof ruleIndex !== "number") {
        return;
      }
      setHighlightState({
        ruleIndex,
        element: e.target as HTMLElement,
      });
      Analytics.dashboardReportHighlightCellHovered({
        ruleIndex,
        depth: row.depth,
        rowName: node?.title,
        reportType,
        columnName: column.id,
      });
    },
    [column.id, node?.title, options?.metadata?.ruleIndex, reportType, row.depth, setHighlightState]
  );
  const handleHoverEnd = useCallback(() => {
    setHighlightState(undefined);
  }, [setHighlightState]);

  return useMemo(() => {
    // --- Determine Link Target ---

    let linkObjectToUse = link;

    // Check conditions for using a special footer link
    const isFooter = variant === "footer";
    const isRelevantFooterCell = isFooter && !isDiff && link;

    if (isRelevantFooterCell) {
      // Check if this specific footer cell is the Accrual Revenue Total
      const isAccrualRevenueTotalCell =
        node?.title.includes("Revenue") &&
        reportType === DynamicReportType.ProfitAndLoss &&
        view === "accrual";

      if (isAccrualRevenueTotalCell) {
        // If it is, get the specific link for this total
        linkObjectToUse = getLinkForAccrualRevenueTotal({ row, column, categoriesByPermaKey });
      }
      // If it's a relevant footer cell but not the accrual total,
      // linkObjectToUse remains the original `link` from props.
    }

    // --- Calculate Href if Needed ---

    const hasLinkObject = Boolean(linkObjectToUse);
    const isAccrualRevenueSourceWithTimePeriod = isAccrualRevenueSource && hasTimePeriod;
    const shouldRenderAccrualRevenueLink = hasLinkObject && isAccrualRevenueSourceWithTimePeriod;
    const shouldRenderFooterLink = hasLinkObject && isRelevantFooterCell;

    // Determine if *any* link should be rendered for this cell
    const shouldRenderLink = shouldRenderAccrualRevenueLink || shouldRenderFooterLink;
    let calculatedHref: string | undefined = undefined;

    // Only construct the href if we determined a link should be rendered and have a link object
    if (shouldRenderLink) {
      const linkPathname = linkObjectToUse!.pathname ?? "";
      const linkQuery =
        linkObjectToUse?.query && typeof linkObjectToUse.query === "object"
          ? linkObjectToUse.query
          : {};

      // Construct the final href using the determined link object and companyId
      const { href, error } = constructHref(linkPathname, companies.length, {
        ...linkQuery,
        companyId, // From ActiveCompanyProvider
      });

      if (error) {
        reportError(
          `BreakoutCell.tsx error calling constructHref function inside useMemo: ${error.message}`,
          error.payload
        );
      } else {
        calculatedHref = href;
      }
    }

    // --- Event Breakout and Interaction Setup ---

    // Determine if event breakout interaction is enabled based on feature flags and filters
    const hasFilter = filter?.segments?.segmentIds?.length && filter.segments.segmentIds.length > 0;
    const eventBreakoutEnabled = isPosthogFeatureFlagEnabled(FeatureFlag.EnableEventBreakout)
      ? true
      : !hasFilter || activeTab !== DynamicReportType.ProfitAndLoss;

    // Click handler for triggering breakout view
    const onClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      if (!eventBreakoutEnabled) return; // Do nothing if breakout is disabled

      if (row && hasBreakout) {
        Analytics.dashboardReportCellClicked({
          depth: row.depth,
          rowName: node?.title,
          reportType,
          columnName: column.id,
          highlightModeEnabled: deltaOptions.enabled,
        });

        setBreakoutData({ categoriesByPermaKey, stickyOptions, row, column, rowsById });
      }
    };

    // --- Cell Props ---

    const cellProps = {
      variant,
      hasChildren,
      children: value,
      open: eventBreakoutEnabled ? active : false, // Show as open if active and breakout enabled
      canOpen: eventBreakoutEnabled ? hasBreakout && !options?.vendorHighlight : false, // Can open if breakout enabled/possible
      highlight: options?.highlight ?? false,
      vendorHighlight: options?.vendorHighlight ?? false,
      onMouseEnter: handleHoverStart,
      onMouseLeave: handleHoverEnd,
      onFocus: handleHoverStart,
      onBlur: handleHoverEnd,
    };

    // --- Render Logic ---

    let content = <InteractiveCell onClick={onClick} root={row.depth === 0} {...cellProps} />;

    // Check if we should wrap the cell with a NextLink
    if (shouldRenderLink && calculatedHref) {
      content = (
        <NextLink href={calculatedHref} passHref legacyBehavior>
          <InteractiveCell
            as="a" // anchor tag
            external
            target="_blank"
            rel="noopener noreferrer"
            {...cellProps}
            // Footer links are always considered 'openable' visually,
            // otherwise, use the calculated canOpen state.
            canOpen={isRelevantFooterCell ? true : cellProps.canOpen}
          />
        </NextLink>
      );
    }

    return <>{renderBreakout(content, props)}</>;
  }, [
    variant,
    hasChildren,
    value,
    active,
    hasBreakout,
    options?.highlight,
    isDiff,
    link,
    renderBreakout,
    props,
    row,
    node?.title,
    reportType,
    column,
    deltaOptions.enabled,
    setBreakoutData,
    rowsById,
    options?.vendorHighlight,
    view,
    isAccrualRevenueSource,
    hasTimePeriod,
    categoriesByPermaKey,
    filter?.segments,
    activeTab,
    stickyOptions,
    companyId,
    companies.length,
    handleHoverStart,
    handleHoverEnd,
    options?.metadata?.ruleIndex,
  ]);
};
